import { Component, Input, OnChanges, OnInit, SimpleChanges, computed, inject, signal } from '@angular/core';
import { Filter, Displayable } from '../../models/filter-models';
import { DisplayType, EquipmentTieringMode, JewelType } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { Tier, TieredEquipment, BaseType, ItemData } from '../../models/item-data-models';
import { EquipmentType } from '../../models/equipment-type.enum';
import { RuleId } from '../../models/rule-id';
import { FilterService } from '../../services/filter.service';
import { TierStyleComponent } from '../../common/tier-style/tier-style.component';
import { EquipmentTierService } from '../../services/tiering/equipment-tier.service';
import { TooltipWrapperComponent } from '../../common/tooltip-wrapper/tooltip-wrapper.component';
import { SelectComponent, SelectOptionComponent } from '../../common/select/select.component';
import { EquipmentLabelComponent } from '../../common/equipment-label/equipment-label.component';
import { CustomRuleStylePickerComponent } from '../../common/style-picker/custom-rule-style-picker/custom-rule-style-picker.component';
import { BaseComponent } from '../../common/base.component';
import { getTierColor } from '../../common/helper';
import { ItemDataService } from '../../services/item-data.service';
import { hasMovedFromOriginalTier } from '../tier-helpers';
import { NavigationService } from '../../services/navigation.service';
import { CtrlKeyService } from '../../services/ctrl-key.service';
import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';

interface TieredEquipmentVm extends TieredEquipment {
  icon?: string;
  ruleId?: RuleId;
  hasCustomRuleStyle?: boolean;
}

interface TierItemTypeGroupVm {
  itemType: string;
  baseTypes: TieredEquipmentVm[];
}

interface EquipmentSectionVm {
  sectionName: string;
  itemTypes: TierItemTypeGroupVm[];
}

interface TierDataVm {
  tier: Tier;
  equipmentSections: EquipmentSectionVm[];
  name: string;
  styleDialogTitle: string;
  isDragOver?: boolean;
  dragCounter?: number;
  cosmetics: Displayable;
  exampleName: string;
  show: boolean;
  tierColor?: string;
}

@Component({
  selector: 'app-equipment-tiers',
  templateUrl: './equipment-tiers.component.html',
  styleUrls: ['./equipment-tiers.component.scss', '../tier-tab-common.scss'],
  imports: [FormsModule, TierStyleComponent, TooltipWrapperComponent,
    SelectComponent, SelectOptionComponent, EquipmentLabelComponent, CustomRuleStylePickerComponent, DynamicContentComponent]
})
export class EquipmentTiersComponent extends BaseComponent implements OnChanges, OnInit {
  @Input({ required: true }) filter!: Filter;
  @Input() changedOnly = false;

  equipmentTierData: TieredEquipment[] = [];
  tierList: TierDataVm[] = [];
  draggedItem: TieredEquipmentVm | null = null;
  tierColumns: TierDataVm[][] = [];

  selectedSelectionFilter = 'All';
  selectedSlotFilter = 'All';
  selectedCategoryFilter = 'All';

  // Icon URLs for 'my' equipment
  myGearIcon?: string;
  myJewelleryIcon?: string;
  myJewelIcon?: string;
  myFlaskIcon?: string;

  // Icon URLs for 'other' equipment
  otherGearIcon?: string;
  otherJewelleryIcon?: string;
  otherJewelIcon?: string;
  otherFlaskIcon?: string;

  selectionFilters = this.poe2 ? [
    { value: 'All', text: 'All' },
    { value: 'my', text: 'Favourite Equipment' },
    { value: 'other', text: 'Other Equipment' },
    { value: 'maps', text: 'Waystones' },
    { value: 'items', text: 'Other Items' }
  ] : [
    { value: 'All', text: 'All' },
    { value: 'my', text: 'Favourite Equipment' },
    { value: 'other', text: 'Other Equipment' },
    { value: 'maps', text: 'Maps' },
    { value: 'gems', text: 'Gems' },
    { value: 'items', text: 'Other Items' }
  ];

  private selectionPrefixNames: Record<string, string> = {
    'my': 'Favourite',
    'other': 'Other',
  };

  // Mapping from selection values to display names
  private selectionDisplayNames: Record<string, string> = {
    'my': 'Favourite Equipment',
    'other': 'Other Equipment',
    'items': 'Other Items',
    'maps': 'Maps',
    'gems': 'Gems'
  };

  // Order for sections (used when displaying multiple sections)
  private selectionOrder: string[] = ['my', 'other', 'maps', 'gems', 'items'];

  slotFilters = this.poe2 ? [
    { value: 'All', text: 'All' },
    { value: 'gear', text: 'Gear' },
    { value: 'jewellery', text: 'Jewellery' },
    { value: 'jewel', text: 'Jewels' },
    { value: 'flask', text: 'Flasks' },
    { value: 'charm', text: 'Charms' },
  ] : [
    { value: 'All', text: 'All' },
    { value: 'gear', text: 'Gear' },
    { value: 'jewellery', text: 'Jewellery' },
    { value: 'jewel', text: 'Jewels' },
    { value: 'flask', text: 'Flasks' },
  ];

  categoryFilters = this.poe2 ? [
    { value: 'All', text: 'All' },
    { value: 'regular', text: 'Regular' },
    { value: 'rare', text: 'Rare Unidentified' },
    { value: 'magic', text: 'Magic Unidentified' },
    { value: 'crafting', text: 'Crafting' },
    { value: 'identified', text: 'Identified' },
    { value: 'leveling', text: 'Leveling' },
    { value: 'salvaging', text: 'Salvaging' },
  ] : [
    { value: 'All', text: 'All' },
    { value: 'regular', text: 'Regular' },
    { value: 'crafting', text: 'Crafting' },
    { value: 'identified', text: 'Identified' },
    { value: 'leveling', text: 'Leveling' },
    { value: 'salvaging', text: 'Recipes' },
  ];

  // Map of equipment types to their specific icons
  equipmentIconMap: Partial<Record<EquipmentType, string>> = {
    [EquipmentType.OtherJewelleryExcellentClusterJewelBases]: '/img/gear/jewels/newgembase3.webp',
    [EquipmentType.OtherJewelleryGoodClusterJewelBases]: '/img/gear/jewels/newgembase1.webp',
    [EquipmentType.ItemsHeistEnchantedBlueprints]: '/img/currency/heist/blueprintnotapproved.webp',
    [EquipmentType.ItemsHeistEnchantedContracts]: '/img/currency/heist/contractitem.webp',
    [EquipmentType.ItemsHeistBlueprints]: '/img/currency/heist/blueprintnotapproved.webp',
    [EquipmentType.ItemsHeistContracts]: '/img/currency/heist/contractitem.webp',
    [EquipmentType.ItemsRoguesMarkers]: '/img/currency/heist/heistcoincurrency.webp',
    [EquipmentType.ItemsTrialItems]: '/img/gear/relics/relicbase1x2.webp',
    // POE1 Gems
    [EquipmentType.GemsTopTierAwakened]: '/img/gear/gems/support/EmpowerPlus.webp',
    [EquipmentType.GemsTopTierSpecialSupport]: '/img/gear/gems/support/Empower.webp',
    [EquipmentType.GemsTopTierExceptionalSupport]: '/img/gear/gems/support/EclipseSupport.png',
    [EquipmentType.GemsOtherExceptionalSupport]: '/img/gear/gems/support/BloodsoakedBannerSupport.png',
    [EquipmentType.GemsImbued]: '/img/gear/gems/Elementalhit.webp',
    // POE1 Maps
    [EquipmentType.MapsTier17]: '/img/maps/map-t17.png',
    [EquipmentType.MapsTier16Influenced]: '/img/maps/map-influenced.png',
    [EquipmentType.MapsTier16MemoryInfluenced]: '/img/maps/map-memory-influenced.png',
    [EquipmentType.MapsTier16Blighted]: '/img/maps/map-blighted.png',
    [EquipmentType.MapsTier16BlightRavaged]: '/img/maps/map-blight-ravaged.png',
    [EquipmentType.MapsTier16Delirium]: '/img/maps/map-delirium.png',
    [EquipmentType.MapsTier16EightMod]: '/img/maps/map-normal.png',
    // [EquipmentType.MapsLowTierInfluenced]: '/img/maps/map-influenced.png',
    [EquipmentType.MapsLowTierMemoryInfluenced]: '/img/maps/map-memory-influenced.png',
    [EquipmentType.MapsLowTierBlighted]: '/img/maps/map-blighted.png',
    [EquipmentType.MapsLowTierDelirium]: '/img/maps/map-delirium.png',
    [EquipmentType.MapsTier11]: '/img/maps/map-normal.png',
    [EquipmentType.MapsTier6]: '/img/maps/map-normal.png',
    [EquipmentType.MapsTier1]: '/img/maps/map-normal.png',
    [EquipmentType.NormalGoldStacks]: '/img/currency/CoinPileTier2.webp',
    [EquipmentType.LargeGoldStacks]: '/img/currency/CoinPileTier2.webp',
    [EquipmentType.HugeGoldStacks]: '/img/currency/CoinPileTier2.webp',
    // POE2 Waystones
    [EquipmentType.ItemsWaystonesRare]: '/img/gear/maps/endgamemaps/endgamemap15.webp',
    // [EquipmentType.ItemsWaystonesDelirium]: '/img/gear/maps/endgamemaps/endgamemap15.webp',
    [EquipmentType.ItemsWaystonesOther]: '/img/gear/maps/endgamemaps/endgamemap15.webp',
  };

  DisplayType = DisplayType;
  Tier = Tier;

  private readonly navigationService = inject(NavigationService);
  private readonly ctrlKeyService = inject(CtrlKeyService);

  private readonly hoveredEquipmentType = signal<EquipmentType | null>(null);
  readonly ruleNavActive = computed(() =>
    this.settings.enableRuleNavigation() && this.ctrlKeyService.isCtrlHeld()
  );

  constructor(
    private filterService: FilterService,
    private equipmentTierService: EquipmentTierService,
    private itemDataService: ItemDataService
  ) {
    super();
  }

  isRuleNavHovered(equipment: TieredEquipmentVm): boolean {
    return this.ruleNavActive()
      && !!equipment.ruleId
      && this.hoveredEquipmentType() === equipment.equipmentType;
  }

  onItemMouseEnter(equipment: TieredEquipmentVm): void {
    this.hoveredEquipmentType.set(equipment.equipmentType);
  }

  onItemMouseLeave(): void {
    this.hoveredEquipmentType.set(null);
  }

  onItemClick(event: MouseEvent, equipment: TieredEquipmentVm): void {
    if (!this.settings.enableRuleNavigation()) return;
    if (!(event.ctrlKey || event.metaKey)) return;
    if (!equipment.ruleId) return;

    event.preventDefault();
    event.stopPropagation();
    this.navigationService.navigateToFilterTitle(equipment.ruleId);
  }

  ngOnInit(): void {
    this.retrieveFilterIcons();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['changedOnly']) {
      this.retrieveFilterIcons();
      this.buildTierList();
    }
  }

  private retrieveFilterIcons(): void {
    const itemData = this.itemDataService.getItemData();

    // Retrieve 'my' equipment icons
    // Gear: weapon first, then armour as backup
    if (this.filter.weaponFilters.length > 0) {
      const weaponFilter = this.filter.weaponFilters[0];
      if (weaponFilter.weaponType && weaponFilter.weaponType.length > 0) {
        const weaponClass = weaponFilter.weaponType[0];
        const weaponItem = itemData.find(i => i.itemClass === weaponClass);
        if (weaponItem && weaponItem.baseTypes.length > 0) {
          this.myGearIcon = weaponItem.baseTypes[0].image;
        }
      }
    } else if (this.filter.armourFilters.length > 0) {
      const armourFilter = this.filter.armourFilters[0];
      if (armourFilter.armourType && armourFilter.armourType.length > 0) {
        const armourSlot = armourFilter.armourType[0];
        const armourItem = itemData.find(i => i.itemClass === armourSlot);
        if (armourItem && armourItem.baseTypes.length > 0) {
          this.myGearIcon = armourItem.baseTypes[0].image;
        }
      }
    }

    // Jewellery
    if (this.filter.jewelleryFilters.length > 0) {
      const jewelleryFilter = this.filter.jewelleryFilters[0];
      const jewelleryItem = itemData.find(i => i.itemType === jewelleryFilter.jewelleryType);
      if (jewelleryItem && jewelleryItem.baseTypes.length > 0) {
        if (jewelleryFilter.jewelleryBaseTypes.includes('All')) {
          this.myJewelleryIcon = jewelleryItem.baseTypes[0].image;
        } else if (jewelleryFilter.jewelleryBaseTypes.length > 0) {
          const selectedBaseType = jewelleryFilter.jewelleryBaseTypes[0];
          const baseType = jewelleryItem.baseTypes.find(b => b.name === selectedBaseType);
          if (baseType) {
            this.myJewelleryIcon = baseType.image;
          }
        }
      }
    }

    // Jewel
    if (this.filter.jewelFilters.length > 0) {
      const jewelFilter = this.filter.jewelFilters[0];
      if (jewelFilter.jewelType && jewelFilter.jewelType.length > 0) {
        this.myJewelIcon = this.getJewelIcon(jewelFilter.jewelType[0]);
      }
    }

    // Flask
    if (this.filter.flaskFilters.length > 0) {
      const flaskFilter = this.filter.flaskFilters[0];
      const flaskItem = itemData.find(i => i.itemClass === flaskFilter.flaskClass);
      if (flaskItem && flaskItem.baseTypes.length > 0) {
        if (flaskFilter.flaskBaseTypes.includes('All')) {
          this.myFlaskIcon = flaskItem.baseTypes[0].image;
        } else if (flaskFilter.flaskBaseTypes.length > 0) {
          const selectedBaseType = flaskFilter.flaskBaseTypes[0];
          const baseType = flaskItem.baseTypes.find(b => b.name === selectedBaseType);
          if (baseType) {
            this.myFlaskIcon = baseType.image;
          }
        }
      }
    }

    this.otherGearIcon = '/img/all-weapons.png';
    this.otherJewelleryIcon = '/img/all-jewellery.png';
    this.otherJewelIcon = '/img/all-jewels.png';
    this.otherFlaskIcon = '/img/all-flasks.png';
  }

  private getJewelIcon(jewelType: JewelType): string | undefined {
    const jewelIconMap: Record<string, string> = this.poe2 ? {
      'Emerald': '/img/gear/jewels/emeraldjewel.webp',
      'Ruby': '/img/gear/jewels/rubyjewel.webp',
      'Sapphire': '/img/gear/jewels/sapphirejewel.webp',
      'Diamond': '/img/gear/jewels/diamondjewel.webp',
      'Time-Lost Emerald': '/img/gear/jewels/specialemeraldjewel.webp',
      'Time-Lost Ruby': '/img/gear/jewels/specialrubyjewel.webp',
      'Time-Lost Sapphire': '/img/gear/jewels/specialsapphirejewel.webp',
      'Time-Lost Diamond': '/img/gear/jewels/timelostdiamondjewel.webp',
    } : {
      'Crimson Jewel': '/img/gear/jewels/basicstr.webp',
      'Viridian Jewel': '/img/gear/jewels/basicdex.webp',
      'Cobalt Jewel': '/img/gear/jewels/basicint.webp',
      'Large Cluster Jewel': '/img/gear/jewels/newgembase3.webp',
      'Medium Cluster Jewel': '/img/gear/jewels/newgembase2.webp',
      'Small Cluster Jewel': '/img/gear/jewels/newgembase1.webp',
      'Murderous Eye Jewel': '/img/gear/jewels/murderouseye.webp',
      'Searching Eye Jewel': '/img/gear/jewels/searchingeye.webp',
      'Hypnotic Eye Jewel': '/img/gear/jewels/rivetedeye.webp',
      'Ghastly Eye Jewel': '/img/gear/jewels/ghastlyeye.webp',
    };
    return jewelIconMap[jewelType];
  }

  private createTieredEquipmentVm(equipment: TieredEquipment): TieredEquipmentVm {
    const vm: TieredEquipmentVm = { ...equipment };

    const meta = this.equipmentTierService.getMetadataForEquipmentType(equipment.equipmentType);
    vm.ruleId = meta?.ruleId;
    vm.hasCustomRuleStyle = !!vm.ruleId && this.filter.customRuleStyles.some(s => s.ruleId === vm.ruleId && s.custom);

    // Handle equipment types with specific icons
    if (equipment.equipmentType && this.equipmentIconMap[equipment.equipmentType]) {
      vm.icon = this.equipmentIconMap[equipment.equipmentType];
      return vm;
    }

    // Set icon based on slot and selection
    if (equipment.selection === 'maps') {
      vm.icon = '/img/maps/map-normal.png';
      return vm;
    } else if (equipment.selection === 'gems') {
      vm.icon = '/img/gear/gems/Cleave.webp';
      return vm;
    } else if (equipment.selection === 'my') {
      switch (equipment.slot) {
        case 'gear':
          vm.icon = this.myGearIcon;
          break;
        case 'jewellery':
          vm.icon = this.myJewelleryIcon;
          break;
        case 'jewel':
          vm.icon = this.myJewelIcon;
          break;
        case 'flask':
          vm.icon = this.myFlaskIcon;
          break;
      }
    } else if (equipment.selection === 'other') {
      switch (equipment.slot) {
        case 'gear':
          vm.icon = this.otherGearIcon;
          break;
        case 'jewellery':
          vm.icon = this.otherJewelleryIcon;
          break;
        case 'jewel':
          vm.icon = this.otherJewelIcon;
          break;
        case 'flask':
          vm.icon = this.otherFlaskIcon;
          break;
      }
    } else if (equipment.selection === 'items') {
      // Items use their specific icons from equipmentIconMap if available
      // Otherwise, no default icon is set
    }

    return vm;
  }


  buildTierList(): void {
    this.equipmentTierData = this.equipmentTierService.getEquipmentTierData();

    // Build TierData structure from the flat list
    const EQUIPMENT_ORDERED_TIERS = [Tier.SPurple, Tier.ARed, Tier.BOrange, Tier.CYellow, Tier.DWhite, Tier.EVisible];

    this.tierList = EQUIPMENT_ORDERED_TIERS.map(tier => {
      const tierConfig = this.filter.equipmentTierCosmetics.find(c => c.tier === tier)!;
      const tierName = tierConfig.displayOptions.hidden
        ? 'Hidden'
        : tierConfig.displayOptions.cosmeticOptions?.highlight
          ? tierConfig.displayOptions.cosmeticOptions?.highlightColor
          : 'Visible';

      // Filter equipment by tier and selected filters
      const filteredEquipment = this.equipmentTierData
        .filter(equipment => equipment.tier === tier)
        .filter(equipment => !this.changedOnly || hasMovedFromOriginalTier(equipment.originalTier, tier))
        .filter(equipment => {
          if (this.selectedSelectionFilter !== 'All' && equipment.selection !== this.selectedSelectionFilter) {
            return false;
          }
          if (this.selectedSlotFilter !== 'All' && equipment.slot !== this.selectedSlotFilter) {
            return false;
          }
          if (this.selectedCategoryFilter !== 'All' && equipment.category !== this.selectedCategoryFilter) {
            return false;
          }
          return true;
        });

      // Define slot order for sorting
      const slotOrder = ['gear', 'jewellery', 'jewel', 'flask', 'charm', 'item'];

      // Sort equipment by selection order, then by slot order
      const sortedEquipment = [...filteredEquipment].sort((a, b) => {
        const selectionCompare = this.selectionOrder.indexOf(a.selection) - this.selectionOrder.indexOf(b.selection);
        if (selectionCompare !== 0) return selectionCompare;

        const slotCompare = slotOrder.indexOf(a.slot) - slotOrder.indexOf(b.slot);
        if (slotCompare !== 0) return slotCompare;

        return 0;
      });

      // Group equipment by selection value dynamically
      const equipmentBySelection = new Map<string, TieredEquipmentVm[]>();
      sortedEquipment.forEach(equipment => {
        const selection = equipment.selection;
        if (!equipmentBySelection.has(selection)) {
          equipmentBySelection.set(selection, []);
        }
        equipmentBySelection.get(selection)!.push(this.createTieredEquipmentVm(equipment));
      });

      // Create equipment sections dynamically
      const equipmentSections: EquipmentSectionVm[] = [];

      // Helper function to create equipment section
      const createEquipmentSection = (equipment: TieredEquipmentVm[], sectionName: string): EquipmentSectionVm | null => {
        if (equipment.length === 0) return null;

        const groupMap = new Map<string, TieredEquipmentVm[]>();
        equipment.forEach(item => {
          // For items/maps/gems that might not have a slot in slotFilters, use a simpler grouping
          const slotFilter = this.slotFilters.find(filter => filter.value === item.slot);
          let groupName: string;

          if (slotFilter) {
            const prefix = this.selectionPrefixNames[item.selection]
              ?? item.selection.charAt(0).toUpperCase() + item.selection.slice(1);
            groupName = prefix + ' ' + slotFilter.text;
          } else {
            // For items/maps/gems without standard slots, group by selection only
            // This will create groups like "Items", "Maps", "Gems" without slot suffix
            groupName = this.selectionDisplayNames[item.selection] ||
                       item.selection.charAt(0).toUpperCase() + item.selection.slice(1);
          }

          if (!groupMap.has(groupName)) {
            groupMap.set(groupName, []);
          }
          groupMap.get(groupName)!.push(item);
        });

        const itemTypes: TierItemTypeGroupVm[] = Array.from(groupMap.entries()).map(([groupName, items]) => ({
          itemType: groupName,
          baseTypes: items
        }));

        return {
          sectionName,
          itemTypes
        };
      };

      // Create sections for each selection type in order
      for (const selection of this.selectionOrder) {
        const equipment = equipmentBySelection.get(selection);
        if (equipment && equipment.length > 0) {
          const sectionName = this.selectionDisplayNames[selection] || selection.charAt(0).toUpperCase() + selection.slice(1);
          const section = createEquipmentSection(equipment, sectionName);
          if (section) {
            equipmentSections.push(section);
          }
        }
      }

      return {
        tier,
        equipmentSections,
        name: (tier + '-Tier - ') + tierName,
        styleDialogTitle: tier + '-Tier',
        cosmetics: tierConfig,
        exampleName: equipmentSections[0]?.itemTypes[0]?.baseTypes[0]?.displayName || '',
        show: tierConfig.displayOptions.hidden,
        tierColor: getTierColor(this.filter, tier, false, 'equipmentTierCosmetics'),
      } as TierDataVm;
    });

    // Organize into 6 columns: one tier per column
    this.tierColumns = [
      [], // S
      [], // A
      [], // B
      [], // C
      [], // D
      [], // E
    ];

    for (let tier of this.tierList) {
      if (tier.tier === Tier.SPurple) {
        this.tierColumns[0].push(tier);
      } else if (tier.tier === Tier.ARed) {
        this.tierColumns[1].push(tier);
      } else if (tier.tier === Tier.BOrange) {
        this.tierColumns[2].push(tier);
      } else if (tier.tier === Tier.CYellow) {
        this.tierColumns[3].push(tier);
      } else if (tier.tier === Tier.DWhite) {
        this.tierColumns[4].push(tier);
      } else if (tier.tier === Tier.EVisible) {
        this.tierColumns[5].push(tier);
      }
    }
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  onDragStart(event: DragEvent, equipment: TieredEquipmentVm) {
    if (event.ctrlKey || event.metaKey || this.ctrlKeyService.isCtrlHeld()) {
      event.preventDefault();
      return;
    }
    this.draggedItem = equipment;
    event.dataTransfer?.setData('equipment', JSON.stringify(equipment));
  }

  onDragEnd() {
    this.draggedItem = null;
  }

  onDragEnter(event: DragEvent, tier: TierDataVm) {
    event.preventDefault();
    tier.dragCounter = (tier.dragCounter || 0) + 1;
    tier.isDragOver = true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragLeave(event: DragEvent, tier: TierDataVm) {
    tier.dragCounter = (tier.dragCounter || 0) - 1;
    if (tier.dragCounter === 0) {
      tier.isDragOver = false;
    }
  }

  onDrop(event: DragEvent, targetTier: TierDataVm) {
    event.preventDefault();
    targetTier.dragCounter = 0;
    targetTier.isDragOver = false;
    const equipmentJson = event.dataTransfer!.getData('equipment');
    if (!equipmentJson) return;
    const equipment = JSON.parse(equipmentJson) as TieredEquipmentVm;
    this.moveItemToTier(equipment, targetTier.tier || Tier.EVisible);
    this.draggedItem = null;
  }


  resetToOriginalTier(equipment: TieredEquipmentVm) {
    if (equipment.equipmentType) {
      this.filterService.resetEquipmentItemTier(equipment.equipmentType);
      this.buildTierList();
    }
  }

  moveItemToTier(equipment: TieredEquipmentVm, targetTier: Tier) {
    if (equipment.tier === targetTier || !equipment.equipmentType) return;
    this.filterService.updateEquipmentItemTier(equipment.equipmentType, targetTier);
    this.buildTierList();
  }

  selectTieringMode(mode: EquipmentTieringMode) {
    this.equipmentTierService.setTieringMode(mode, this.filter);
    this.buildTierList();
    this.updateFilter();
  }

  hideInfoMessage() {
    this.settings.dismissItemsTierListMessage();
  }
}

